import { useTranslation } from 'react-i18next';
import { X, Circle, Flag, User, Calendar, Tag, Check, Type, Layers, Layout } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { InlineDatePicker } from '@/shared/components/custom/form-field/InlineDatePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/core/lib/utils';
import { useProjectStatuses } from '../../hooks/useProjectStatuses';
import { useProjectMembers } from '@/features/projects/public';
import { useTags } from '@/features/tags/public';
import { useBoards, useBoardGroups } from '@/features/boards/public';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { PALETTE } from '@/core/lib/colors';
import { getColorValue } from '@/core/lib/color-contrast';
import type { FilterCondition, FilterFieldType, FilterOperator, TaskPriority } from '../../types';

const FIELD_ICON: Record<FilterFieldType, React.FC<{ className?: string }>> = {
  status: Circle,
  priority: Flag,
  assignee: User,
  dueDate: Calendar,
  tag: Tag,
  title: Type,
  group: Layers,
  board: Layout,
  startDate: Calendar,
};

const PRIORITIES: TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

function deterministicColor(userId: string): string {
  const hash = userId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

function SelectionIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border',
        checked ? 'border-primary bg-primary' : 'border-muted-foreground/40',
      )}
    >
      {checked && <Check className="size-2.5 text-primary-foreground" strokeWidth={3} />}
    </span>
  );
}

function StatusValueCell({
  boardId,
  values,
  onChange,
}: {
  boardId: string;
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation('tasks');
  const { statuses } = useProjectStatuses(boardId);
  const TYPE_ORDER = { todo: 0, 'in-progress': 1, done: 2 } as const;
  const sorted = [...statuses].sort(
    (a, b) => (TYPE_ORDER[a.type] ?? 0) - (TYPE_ORDER[b.type] ?? 0),
  );
  const selected = sorted.filter((s) => values.includes(s.id));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-filter-status-picker"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{t('filterPanel.select')}</span>
          ) : (
            <>
              {selected.slice(0, 2).map((s) => (
                <span
                  key={s.id}
                  className="flex shrink-0 items-center gap-1 rounded-full bg-accent px-1.5 py-0.5 text-[10px]"
                >
                  <span
                    className="size-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                  {s.name}
                </span>
              ))}
              {selected.length > 2 && (
                <span className="shrink-0 text-muted-foreground">+{selected.length - 2}</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        {sorted.map((status) => {
          const checked = values.includes(status.id);
          return (
            <div
              key={status.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
              onClick={() =>
                onChange(checked ? values.filter((v) => v !== status.id) : [...values, status.id])
              }
            >
              <SelectionIndicator checked={checked} />
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: status.color }}
              />
              <span className="text-xs">{status.name}</span>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function PriorityValueCell({
  values,
  onChange,
}: {
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation('tasks');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-filter-priority-picker"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
        >
          {values.length === 0 ? (
            <span className="text-muted-foreground">{t('filterPanel.select')}</span>
          ) : (
            <>
              {values.slice(0, 2).map((p) => (
                <span key={p} className="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px]">
                  {t(`priority.${p}`)}
                </span>
              ))}
              {values.length > 2 && (
                <span className="shrink-0 text-muted-foreground">+{values.length - 2}</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-1" align="start">
        {PRIORITIES.map((p) => {
          const checked = values.includes(p);
          return (
            <div
              key={p}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
              onClick={() => onChange(checked ? values.filter((v) => v !== p) : [...values, p])}
            >
              <SelectionIndicator checked={checked} />
              <span className="text-xs">{t(`priority.${p}`)}</span>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function AssigneeValueCell({
  projectId,
  values,
  onChange,
}: {
  projectId: string;
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation('tasks');
  const { data: members = [] } = useProjectMembers(projectId);
  const selected = members.filter((m) => values.includes(m.userId));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-filter-assignee-picker"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{t('filterPanel.select')}</span>
          ) : (
            <>
              {selected.slice(0, 3).map((m) => (
                <UserAvatar
                  key={m.userId}
                  name={m.displayName}
                  color={deterministicColor(m.userId)}
                  src={m.avatarUrl ?? undefined}
                  size="xs"
                />
              ))}
              {selected.length > 3 && (
                <span className="shrink-0 text-muted-foreground">+{selected.length - 3}</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          {members.map((member) => {
            const checked = values.includes(member.userId);
            return (
              <div
                key={member.userId}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                onClick={() =>
                  onChange(
                    checked
                      ? values.filter((v) => v !== member.userId)
                      : [...values, member.userId],
                  )
                }
              >
                <SelectionIndicator checked={checked} />
                <UserAvatar
                  name={member.displayName}
                  color={deterministicColor(member.userId)}
                  src={member.avatarUrl ?? undefined}
                  size="xs"
                />
                <span className="text-xs">{member.displayName}</span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TagValueCell({
  projectId,
  values,
  onChange,
}: {
  projectId: string;
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation('tasks');
  const { data } = useTags(projectId, {});
  const tags = data?.items ?? [];
  const selected = tags.filter((tag) => values.includes(tag.id ?? ''));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-filter-tag-picker"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{t('filterPanel.select')}</span>
          ) : (
            <>
              {selected.slice(0, 2).map((tag) => {
                const colorValue = getColorValue(tag.color ?? '');
                return (
                  <span
                    key={tag.id}
                    className="flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
                    style={{
                      backgroundColor: colorValue ? `${colorValue}20` : undefined,
                      color: colorValue ?? undefined,
                    }}
                  >
                    <span
                      className="size-1.5 shrink-0 rounded-full"
                      style={{ background: colorValue ?? undefined }}
                    />
                    {tag.name}
                  </span>
                );
              })}
              {selected.length > 2 && (
                <span className="shrink-0 text-muted-foreground">+{selected.length - 2}</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        <div className="max-h-40 overflow-y-auto">
          {tags.map((tag) => {
            const tagId = tag.id ?? '';
            const checked = values.includes(tagId);
            const colorValue = getColorValue(tag.color ?? '');
            return (
              <div
                key={tag.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                onClick={() =>
                  onChange(checked ? values.filter((v) => v !== tagId) : [...values, tagId])
                }
              >
                <SelectionIndicator checked={checked} />
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: colorValue ?? undefined }}
                />
                <span className="text-xs">{tag.name}</span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DateRangeValueCell({
  values,
  onChange,
  fromTestId,
  toTestId,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  fromTestId?: string;
  toTestId?: string;
}) {
  const { t } = useTranslation('tasks');
  const from = values[0] ?? '';
  const to = values[1] ?? '';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-filter-date-range-picker"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
        >
          {!from && !to ? (
            <span className="text-muted-foreground">{t('filterPanel.select')}</span>
          ) : (
            <span className="truncate">
              {from || '—'} → {to || '—'}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3" align="start">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-xs text-muted-foreground">
              {t('filterPanel.from')}
            </span>
            <InlineDatePicker
              value={from}
              data-testid={fromTestId}
              onChange={(v) => onChange([v, to])}
              className="h-7 flex-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-xs text-muted-foreground">
              {t('filterPanel.to')}
            </span>
            <InlineDatePicker
              value={to}
              data-testid={toTestId}
              onChange={(v) => onChange([from, v])}
              className="h-7 flex-1"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TitleValueCell({
  values,
  onChange,
}: {
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation('tasks');
  const value = values[0] ?? '';

  return (
    <Input
      type="text"
      value={value}
      data-testid="tasks-filter-title-input"
      placeholder={t('filterPanel.typePlaceholder')}
      onChange={(e) => onChange([e.target.value])}
      className="h-6 min-w-0 flex-1 text-xs"
    />
  );
}

function GroupValueCell({
  boardId,
  values,
  onChange,
}: {
  boardId?: string;
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation('tasks');
  const { groups } = useBoardGroups(boardId ?? '');
  const selected = groups.filter((g) => values.includes(g.id));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-filter-group-picker"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{t('filterPanel.select')}</span>
          ) : (
            <>
              {selected.slice(0, 2).map((g) => (
                <span
                  key={g.id}
                  className="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px]"
                >
                  {g.name}
                </span>
              ))}
              {selected.length > 2 && (
                <span className="shrink-0 text-muted-foreground">+{selected.length - 2}</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        <div className="max-h-40 overflow-y-auto">
          {groups.map((group) => {
            const checked = values.includes(group.id);
            return (
              <div
                key={group.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                onClick={() =>
                  onChange(checked ? values.filter((v) => v !== group.id) : [...values, group.id])
                }
              >
                <SelectionIndicator checked={checked} />
                <span className="text-xs">{group.name}</span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BoardValueCell({
  projectId,
  values,
  onChange,
}: {
  projectId: string;
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation('tasks');
  const { boards } = useBoards(projectId);
  const validBoards = boards.filter(
    (b): b is typeof b & { id: string; name: string } => !!b.id && !!b.name,
  );
  const selected = validBoards.filter((b) => values.includes(b.id));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-filter-board-picker"
          className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded border border-border px-2 py-0.5 text-xs hover:bg-accent"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{t('filterPanel.select')}</span>
          ) : (
            <>
              {selected.slice(0, 2).map((b) => (
                <span
                  key={b.id}
                  className="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px]"
                >
                  {b.name}
                </span>
              ))}
              {selected.length > 2 && (
                <span className="shrink-0 text-muted-foreground">+{selected.length - 2}</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        <div className="max-h-40 overflow-y-auto">
          {validBoards.map((board) => {
            const checked = values.includes(board.id);
            return (
              <div
                key={board.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                onClick={() =>
                  onChange(checked ? values.filter((v) => v !== board.id) : [...values, board.id])
                }
              >
                <SelectionIndicator checked={checked} />
                <span className="text-xs">{board.name}</span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ConditionRowProps {
  condition: FilterCondition;
  index: number;
  logic: 'and' | 'or';
  projectId: string;
  boardId: string;
  onChange: (values: string[]) => void;
  onOperatorChange: (operator: FilterOperator) => void;
  onRemove: () => void;
}

const FIELDS_WITH_EMPTY_OPERATORS: FilterFieldType[] = [
  'title',
  'assignee',
  'tag',
  'group',
  'board',
  'startDate',
  'dueDate',
];

export function ConditionRow({
  condition,
  index,
  logic,
  projectId,
  boardId,
  onChange,
  onOperatorChange,
  onRemove,
}: ConditionRowProps) {
  const { t } = useTranslation('tasks');
  const Icon = FIELD_ICON[condition.field];
  const supportsEmpty = FIELDS_WITH_EMPTY_OPERATORS.includes(condition.field);
  const isEmptyOperator = condition.operator === 'isEmpty' || condition.operator === 'isNotEmpty';

  return (
    <div
      className="flex items-center gap-1.5 py-0.5"
      data-testid={`tasks-filter-condition-${condition.id}`}
    >
      {index === 0 ? (
        <div className="w-5 shrink-0" />
      ) : (
        <span className="w-5 shrink-0 text-center text-[10px] font-semibold text-muted-foreground">
          {logic === 'and' ? t('filterPanel.logicAnd') : t('filterPanel.logicOr')}
        </span>
      )}

      <div className="flex shrink-0 items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-xs">
        <Icon className="size-3 text-muted-foreground" />
        <span>{t(`filterPanel.fields.${condition.field}`)}</span>
      </div>

      <Select
        value={condition.operator}
        onValueChange={(v) => onOperatorChange(v as FilterOperator)}
      >
        <SelectTrigger
          className="h-auto shrink-0 gap-1 border-border px-1.5 py-0.5 text-xs text-muted-foreground"
          aria-label={t('filterPanel.operatorLabel')}
          data-testid={`tasks-filter-condition-${condition.id}-operator`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="is">{t('filterPanel.operatorIs')}</SelectItem>
          <SelectItem value="isNot">{t('filterPanel.operatorIsNot')}</SelectItem>
          {supportsEmpty && (
            <>
              <SelectItem value="isEmpty" data-testid="tasks-filter-operator-isEmpty">
                {t('filterPanel.operatorIsEmpty')}
              </SelectItem>
              <SelectItem value="isNotEmpty" data-testid="tasks-filter-operator-isNotEmpty">
                {t('filterPanel.operatorIsNotEmpty')}
              </SelectItem>
            </>
          )}
        </SelectContent>
      </Select>

      <div className="flex min-w-0 flex-1">
        {!isEmptyOperator && (
          <>
            {condition.field === 'status' && (
              <StatusValueCell boardId={boardId} values={condition.values} onChange={onChange} />
            )}
            {condition.field === 'priority' && (
              <PriorityValueCell values={condition.values} onChange={onChange} />
            )}
            {condition.field === 'assignee' && (
              <AssigneeValueCell
                projectId={projectId}
                values={condition.values}
                onChange={onChange}
              />
            )}
            {condition.field === 'dueDate' && (
              <DateRangeValueCell
                values={condition.values}
                onChange={onChange}
                fromTestId="tasks-filter-dueDate-from"
                toTestId="tasks-filter-dueDate-to"
              />
            )}
            {condition.field === 'tag' && (
              <TagValueCell projectId={projectId} values={condition.values} onChange={onChange} />
            )}
            {condition.field === 'title' && (
              <TitleValueCell values={condition.values} onChange={onChange} />
            )}
            {condition.field === 'group' && (
              <GroupValueCell boardId={boardId} values={condition.values} onChange={onChange} />
            )}
            {condition.field === 'board' && (
              <BoardValueCell projectId={projectId} values={condition.values} onChange={onChange} />
            )}
            {condition.field === 'startDate' && (
              <DateRangeValueCell
                values={condition.values}
                onChange={onChange}
                fromTestId="tasks-filter-startDate-from"
                toTestId="tasks-filter-startDate-to"
              />
            )}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={onRemove}
        data-testid={`tasks-filter-condition-remove-${condition.id}`}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-destructive"
        aria-label={t('filterPanel.removeCondition')}
      >
        <X className="size-3" />
      </button>
    </div>
  );
}
